import { DownloadIcon, Eye } from "lucide-react";

import { Button } from "@/components/button";
import { VideoThumbnail } from "@/components/video-thumbnail/VideoThumbnail";
import { useDownloadAttachments } from "@/hooks/useDownloadAttachments.tsx";
import { useImagePreview } from "@/hooks/useImagePreview";
import { getExtensionFromFilename } from "@/utils/getExtensionFromMime";

import type { ChatStory } from "../../types";

import { getAttachmentDisplayName } from "../../utils/attachment-display-name";

export function MediaFile({
  data,
  handleOpenPreview,
}: {
  data: ChatStory;
  handleOpenPreview: (id: number) => void;
}) {
  const { mutate } = useDownloadAttachments();

  const displayName = getAttachmentDisplayName(data.attachment, data.comment);

  const { onError: onImageError, src: imageSrc } = useImagePreview({
    preferredVariant: "thumbnail",
    url: data.attachment?.url,
  });

  const isVideo = () => {
    if (!data.attachment?.url && !data.attachment?.name) return false;
    const fileName = data.attachment.name || data.attachment.url || "";
    const videoExtensions = ["mp4", "mov", "avi", "webm", "mkv", "m4v"];

    return videoExtensions.some((ext) =>
      fileName.toLowerCase().includes(ext.toLowerCase()),
    );
  };

  const downloadFile = async () => {
    if (!data.attachment?.url || !data.attachmentId) return;

    mutate({ fallbackUrl: data.attachment.url, fileId: data.attachmentId });
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center gap-1">
        <div
          className="relative group cursor-pointer"
          onClick={() => {
            handleOpenPreview(data.attachment?.id || 0);
          }}
        >
          {isVideo() ? (
            <VideoThumbnail
              attachmentId={data.attachment?.id || 0}
              className="w-full h-full object-cover rounded-sm"
              extension={getExtensionFromFilename(
                data.attachment?.path ||
                  data.attachment?.name ||
                  data.attachment?.url ||
                  "",
              )}
              src={data.attachment?.url ?? ""}
            />
          ) : (
            <img
              alt="Image"
              className="w-full h-full object-cover rounded-sm"
              onError={onImageError}
              src={imageSrc}
            />
          )}
          <div className="absolute rounded-sm inset-0 flex items-center justify-center transition-all duration-300 bg-transparent group-hover:bg-black/50">
            <Eye className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        </div>
        <p
          className="text-xs text-pretty text-text-tertiary truncate max-w-full overflow-hidden whitespace-nowrap"
          title={displayName}
        >
          {displayName}
        </p>
        <Button
          className="border border-button-secondary-border px-6 mt-4"
          onPress={downloadFile}
          variant="plain"
        >
          <DownloadIcon />
          Download
        </Button>
      </div>
    </>
  );
}
